import { Check, ChevronsUpDown } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/utils";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useTranslation } from "react-i18next";

type TimezoneSelectorProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
};

const timezoneOptions: { value: string; label: string }[] =
  // @ts-expect-error - Somehow Intl.supportedValuesOf is not available in the type definitions
  Intl.supportedValuesOf("timeZone").map((timezone: string) => ({
    value: timezone,
    label: timezone.replace(/_/g, " "),
  }));

function TimezoneSelector({ value, onChange, disabled }: TimezoneSelectorProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(timezoneOptions);
  const [scrollRef, setScrollRef] = useState<HTMLDivElement | null>(null);

  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => scrollRef,
    estimateSize: () => 36,
    overscan: 5,
  });

  const virtualOptions = virtualizer.getVirtualItems();

  const selectedLabel = timezoneOptions.find(
    (timezone) => timezone.value === value,
  )?.label;

  const handleSearch = (search: string) => {
    if (!search) {
      setFilteredOptions(timezoneOptions);
      return;
    }

    const query = search.toLowerCase();
    setFilteredOptions(
      timezoneOptions.filter(
        (timezone) =>
          timezone.label.toLowerCase().includes(query) ||
          timezone.value.toLowerCase().includes(query),
      ),
    );
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setFilteredOptions(timezoneOptions);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-10 w-full justify-between font-normal"
          disabled={disabled}
        >
          <span className="truncate">
            {selectedLabel ?? t("congressForm.selectTimezone")}
          </span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false} onKeyDown={handleKeyDown}>
          <CommandInput
            placeholder={t("congressForm.searchTimezone")}
            onValueChange={handleSearch}
          />
          <CommandEmpty>{t("congressForm.noTimezone")}</CommandEmpty>
          <CommandList>
            <CommandGroup
              ref={setScrollRef}
              style={{
                height: Math.min(filteredOptions.length * 36, 300),
                width: "100%",
                overflow: "auto",
              }}
            >
              <div
                style={{
                  height: `${virtualizer.getTotalSize()}px`,
                  width: "100%",
                  position: "relative",
                }}
              >
                {virtualOptions.map((virtualOption) => {
                  const timezone = filteredOptions[virtualOption.index];

                  return (
                    <CommandItem
                      key={timezone.value}
                      value={timezone.value}
                      onSelect={() => {
                        onChange(timezone.value);
                        setOpen(false);
                      }}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: `${virtualOption.size}px`,
                        transform: `translateY(${virtualOption.start}px)`,
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 size-4 shrink-0",
                          value === timezone.value
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                      <span className="truncate">{timezone.label}</span>
                    </CommandItem>
                  );
                })}
              </div>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export { TimezoneSelector };
